/**
 * foot_ik — feet on the ground for the near skinned actors (2026-09-25,
 * visual-realism R8). After the mixer has posed a body for the frame:
 *   1. sample the ground under each foot (absolute x/z → the terrain height);
 *   2. lower the PELVIS by the lower foot's drop (a foot on the downhill side
 *      must be reachable without locking the knee);
 *   3. solve each leg with the engine's two-bone IK (UpLeg → Leg → Foot) so the
 *      ankle lands where the clip's ankle would stand on this ground;
 *   4. tilt the foot to the ground normal, clamped to 20°.
 * Clips are authored on flat ground, so a body on a hillside otherwise floats
 * one foot and buries the other — the "sliding statue on a slope" read.
 *
 * SAFETY (the rules the harness pins): IK is skipped when the body is not
 * standing on the sampled ground (a floor, a deck, a stair — the sampler is the
 * TERRAIN, so a root more than ROOT_OFF_GROUND_M from it is on something else),
 * when a foot would move more than MAX_STEP_M (a ledge, not a slope), and when
 * the clip lifts the foot (a walk's swing phase keeps its clearance: only the
 * PLANTED foot — within PLANT_HEIGHT_M of the root plane — is pinned).
 * `footIkPlan` is the pure core; `applyFootIk` mutates a rig.
 */
import * as THREE from 'three/webgpu';
import { solveTwoBone } from 'enginex/animation/ik/TwoBoneIk.js';

export const ROOT_OFF_GROUND_M = 0.15;
export const MAX_STEP_M = 0.45;
export const MAX_PELVIS_DROP_M = 0.35;
export const PLANT_HEIGHT_M = 0.12;
export const MAX_FOOT_TILT_DEG = 20;
/**
 * THE HOVER LAW (materials, 2026-09-27): a STANDING clip must stand on the ground. The shared Idle, retargeted
 * onto the player-body donor, holds both ankles 0.20–0.23 m over the root plane (the bind ankle is 0.08–0.09 m):
 * the body stood 12 cm in the air and the plan skipped it every frame as 'airborne' (the player-body probe:
 * 15 skips, lastSkip 'airborne'). For a standing clip (`grounded`) the lower ankle's excess over REST_ANKLE_M is a
 * HOVER, taken off both feet and the pelvis — up to MAX_HOVER_M; a clip that lifts its feet higher than that
 * (a hop, a cot, a dangle) means it and keeps its 'airborne' skip. Moving clips are never grounded.
 */
export const REST_ANKLE_M = 0.09;
export const MAX_HOVER_M = 0.18;
/** A standing clip's feet are both on the ground: after the hover a foot counts as planted up to this height (the
 *  retargeted Idle holds one ankle ~3 cm over the other — PLANT_HEIGHT_M would lift it off at random). */
export const STAND_PLANT_M = 0.2;
/** Actors farther than this (m) from the player are not solved (the research's ≤ 15 m ring). */
export const FOOT_IK_RANGE_M = 15;

export interface FootIkPlan { skip: string | null; pelvisDrop: number; dL: number; dR: number; pinL: boolean; pinR: boolean; hover: number }

/**
 * The pure plan. Heights are ABSOLUTE metres: `rootY` the body's root (its
 * feet plane as posed), `groundRoot/L/R` the terrain under the root and each
 * foot, `footL/R` each animated ankle's height ABOVE the root plane. `grounded`: the clip stands still (the
 * hover law may plant it).
 */
export function footIkPlan(rootY: number, groundRoot: number, groundL: number, groundR: number, footL: number, footR: number, grounded = false): FootIkPlan {
  const none = (why: string): FootIkPlan => ({ skip: why, pelvisDrop: 0, dL: 0, dR: 0, pinL: false, pinR: false, hover: 0 });
  if (![rootY, groundRoot, groundL, groundR, footL, footR].every(Number.isFinite)) return none('non-finite');
  if (Math.abs(rootY - groundRoot) > ROOT_OFF_GROUND_M) return none('off-ground'); // a floor / deck / stair above the terrain
  const dL = groundL - groundRoot, dR = groundR - groundRoot;
  if (Math.abs(dL) > MAX_STEP_M || Math.abs(dR) > MAX_STEP_M) return none('ledge');
  const lo = Math.min(footL, footR);
  const hover = grounded && lo > REST_ANKLE_M && lo - REST_ANKLE_M <= MAX_HOVER_M ? lo - REST_ANKLE_M : 0;
  const plant = grounded ? STAND_PLANT_M : PLANT_HEIGHT_M;
  const pinL = footL - hover < plant, pinR = footR - hover < plant;
  if (!pinL && !pinR) return none('airborne'); // mid-jump / both feet swinging
  // lower the pelvis by the lower PLANTED foot's drop (never raise it: a raised pelvis straightens a knee past the clip);
  // a hovering standing clip comes down by its hover as well
  const tL = dL - hover, tR = dR - hover;
  const drops = [pinL ? tL : -hover, pinR ? tR : -hover];
  const pelvisDrop = Math.max(-MAX_PELVIS_DROP_M, Math.min(0, ...drops));
  return { skip: null, pelvisDrop, dL: pinL ? tL : 0, dR: pinR ? tR : 0, pinL, pinR, hover };
}

/** Rotation that tilts a foot from world-up to the ground normal, clamped to MAX_FOOT_TILT_DEG. */
export function footTilt(normal: THREE.Vector3, out = new THREE.Quaternion()): THREE.Quaternion {
  const up = _up.set(0, 1, 0), n = _n.copy(normal).normalize();
  const ang = Math.acos(Math.max(-1, Math.min(1, up.dot(n))));
  const max = (MAX_FOOT_TILT_DEG * Math.PI) / 180;
  if (ang < 1e-5) return out.identity();
  _axis.crossVectors(up, n).normalize();
  return out.setFromAxisAngle(_axis, Math.min(ang, max));
}
/** The tilt angle footTilt applies (radians) — the harness pins the clamp. */
export function footTiltAngle(normal: THREE.Vector3): number { const q = footTilt(normal, _qa); return 2 * Math.acos(Math.min(1, Math.abs(q.w))); }

export interface FootIkRig { hips: THREE.Object3D; upL: THREE.Object3D; legL: THREE.Object3D; footL: THREE.Object3D; upR: THREE.Object3D; legR: THREE.Object3D; footR: THREE.Object3D }
const LEG = (side: 'Left' | 'Right', part: 'UpLeg' | 'Leg' | 'Foot'): RegExp => new RegExp(`(^|[:_.])${side}${part}$`, 'i');
/** Find the Mixamo-named leg chain (the 24-joint Meshy rigs); null when a bone is missing. */
export function findFootIkRig(root: THREE.Object3D): FootIkRig | null {
  const want: Record<keyof FootIkRig, RegExp> = { hips: /(^|[:_.])hips$/i, upL: LEG('Left', 'UpLeg'), legL: LEG('Left', 'Leg'), footL: LEG('Left', 'Foot'), upR: LEG('Right', 'UpLeg'), legR: LEG('Right', 'Leg'), footR: LEG('Right', 'Foot') };
  const found: Partial<FootIkRig> = {};
  root.traverse((o) => { for (const k of Object.keys(want) as (keyof FootIkRig)[]) if (!found[k] && want[k].test(o.name)) found[k] = o; });
  return Object.keys(want).every((k) => found[k as keyof FootIkRig]) ? found as FootIkRig : null;
}

/** Terrain height at ABSOLUTE (x, z); null when unknown. */
export type GroundSampler = (x: number, z: number) => number | null;

export const footIkStats = { solved: 0, skipped: 0, lastSkip: '' };

/**
 * Apply the plan to a posed rig. `absX/absZ` = the root's ABSOLUTE position (the
 * rig's world positions are RENDER space — only differences are used).
 */
export function applyFootIk(root: THREE.Object3D, rig: FootIkRig, absX: number, absY: number, absZ: number, sample: GroundSampler, weight = 1, grounded = false): FootIkPlan | null {
  root.updateMatrixWorld(true);
  const rootW = _rw.setFromMatrixPosition(root.matrixWorld);
  const fl = _fl.setFromMatrixPosition(rig.footL.matrixWorld), fr = _fr.setFromMatrixPosition(rig.footR.matrixWorld);
  const ax = (v: THREE.Vector3): number => absX + (v.x - rootW.x), az = (v: THREE.Vector3): number => absZ + (v.z - rootW.z);
  const g0 = sample(absX, absZ), gl = sample(ax(fl), az(fl)), gr = sample(ax(fr), az(fr));
  if (g0 === null || gl === null || gr === null) { footIkStats.skipped++; footIkStats.lastSkip = 'no-ground'; return null; }
  const plan = footIkPlan(absY, g0, gl, gr, fl.y - rootW.y, fr.y - rootW.y, grounded);
  if (plan.skip) { footIkStats.skipped++; footIkStats.lastSkip = plan.skip; return plan; }
  // 1. pelvis drop, in the hips' parent space (the rig's bones carry Meshy's centimetre scale)
  if (plan.pelvisDrop < 0 && rig.hips.parent) {
    const hw = _hw.setFromMatrixPosition(rig.hips.matrixWorld); hw.y += plan.pelvisDrop * weight;
    rig.hips.parent.worldToLocal(hw); rig.hips.position.copy(hw); rig.hips.updateMatrixWorld(true);
  }
  // 2. legs: the planted ankle goes to its animated height over THIS ground
  const solveLeg = (up: THREE.Object3D, leg: THREE.Object3D, foot: THREE.Object3D, orig: THREE.Vector3, d: number, gx: number, gz: number): void => {
    const target = _t.copy(orig); target.y += d;
    const pole = _p.copy(orig).add(_fwd.set(0, 0, 1).applyQuaternion(root.getWorldQuaternion(_qr)).multiplyScalar(0.6)); pole.y += 0.5; // the knee bends forward
    solveTwoBone({ root: up as THREE.Bone, mid: leg as THREE.Bone, tip: foot as THREE.Bone, targetPos: target, poleTarget: pole, weight });
    // 3. tilt to the ground normal (finite differences on the sampler), clamped
    const e = 0.15; const hx1 = sample(gx + e, gz), hx0 = sample(gx - e, gz), hz1 = sample(gx, gz + e), hz0 = sample(gx, gz - e);
    if (hx1 === null || hx0 === null || hz1 === null || hz0 === null) return;
    _gn.set(-(hx1 - hx0) / (2 * e), 1, -(hz1 - hz0) / (2 * e));
    const tilt = footTilt(_gn, _qt);
    foot.updateWorldMatrix(true, false);
    const fw = foot.getWorldQuaternion(_qf); const nw = _qn.copy(tilt).multiply(fw);
    if (weight < 1) nw.slerp(fw, 1 - weight);
    const pq = foot.parent!.getWorldQuaternion(_qp).invert();
    foot.quaternion.copy(pq.multiply(nw)); foot.updateMatrixWorld(true);
  };
  const flOrig = _flo.copy(fl), frOrig = _fro.copy(fr);
  if (plan.pinL) solveLeg(rig.upL, rig.legL, rig.footL, flOrig, plan.dL, ax(fl), az(fl));
  if (plan.pinR) solveLeg(rig.upR, rig.legR, rig.footR, frOrig, plan.dR, ax(fr), az(fr));
  footIkStats.solved++;
  return plan;
}

const _up = new THREE.Vector3(), _n = new THREE.Vector3(), _axis = new THREE.Vector3(), _qa = new THREE.Quaternion();
const _rw = new THREE.Vector3(), _fl = new THREE.Vector3(), _fr = new THREE.Vector3(), _hw = new THREE.Vector3(), _t = new THREE.Vector3(), _p = new THREE.Vector3();
const _fwd = new THREE.Vector3(), _qr = new THREE.Quaternion(), _gn = new THREE.Vector3(), _qt = new THREE.Quaternion(), _qf = new THREE.Quaternion(), _qn = new THREE.Quaternion(), _qp = new THREE.Quaternion();
const _flo = new THREE.Vector3(), _fro = new THREE.Vector3();

// ── Breathing (R8): an additive chest + clavicle motion on idle / talk / listen ──

export const BREATH_SPINE_DEG = 1.2;
export const BREATH_CLAVICLE_DEG = 0.8;
export const BREATH_HZ_MIN = 0.22, BREATH_HZ_MAX = 0.3;
/** Clips that breathe visibly (standing still): idles, talking, listening, waiting. */
export function breathesIn(clip: string): boolean { return /idle|talk|listen|stand|wait|chat|bored|look/i.test(clip) && !/walk|run|jog|sprint|jump|punch|slash|swing|dance|sleep|lying|lie_/i.test(clip); }
/** The breath signal −1..1 at time t for an actor's phase seed (per-actor rate inside 0.22–0.3 Hz). */
export function breathSignal(t: number, seed: number): number {
  const hz = BREATH_HZ_MIN + (BREATH_HZ_MAX - BREATH_HZ_MIN) * frac(seed * 0.618034);
  return Math.sin(2 * Math.PI * (hz * t + frac(seed * 0.414214)));
}
function frac(x: number): number { return x - Math.floor(x); }

export interface BreathRig { spine2: THREE.Object3D | null; clavL: THREE.Object3D | null; clavR: THREE.Object3D | null }
export function findBreathRig(root: THREE.Object3D): BreathRig {
  const r: BreathRig = { spine2: null, clavL: null, clavR: null };
  root.traverse((o) => {
    if (!r.spine2 && /(^|[:_.])spine2$/i.test(o.name)) r.spine2 = o;
    if (!r.clavL && /(^|[:_.])leftshoulder$/i.test(o.name)) r.clavL = o;
    if (!r.clavR && /(^|[:_.])rightshoulder$/i.test(o.name)) r.clavR = o;
  });
  if (!r.spine2) root.traverse((o) => { if (!r.spine2 && /(^|[:_.])spine1$/i.test(o.name)) r.spine2 = o; });
  return r;
}
/**
 * Add one breath to a posed rig: the chest pitches about the body's RIGHT axis,
 * the clavicles rise about its FORWARD axis (mirrored) — both expressed in each
 * bone's parent space, like the head layer (the bones' own axes are whatever
 * the rig's orientation makes them). `w` 0..1 fades it with the clip.
 */
export function applyBreath(root: THREE.Object3D, rig: BreathRig, signal: number, w: number): void {
  if (w <= 1e-3) return;
  const rootQ = root.getWorldQuaternion(_bq);
  // Meshy / Mixamo bodies face +Z: the body's LEFT is +X (right = forward × up = −X). A turn about +X by +θ
  // tips the chest forward; a turn about +Z by +θ lifts whatever sits on +X (the LEFT clavicle).
  const leftAxis = _br.set(1, 0, 0).applyQuaternion(rootQ), fwd = _bf.set(0, 0, 1).applyQuaternion(rootQ);
  const turn = (bone: THREE.Object3D | null, axisW: THREE.Vector3, deg: number): void => {
    if (!bone?.parent) return;
    const axis = _ba.copy(axisW).applyQuaternion(bone.parent.getWorldQuaternion(_bp).invert()).normalize();
    bone.rotateOnAxis(axis, (deg * Math.PI) / 180 * signal * w);
  };
  turn(rig.spine2, leftAxis, -BREATH_SPINE_DEG);     // inhale: the chest lifts (a slight pitch back)
  turn(rig.clavL, fwd, BREATH_CLAVICLE_DEG);          // …and both shoulders rise (mirrored about the forward axis)
  turn(rig.clavR, fwd, -BREATH_CLAVICLE_DEG);
}
const _bq = new THREE.Quaternion(), _br = new THREE.Vector3(), _bf = new THREE.Vector3(), _ba = new THREE.Vector3(), _bp = new THREE.Quaternion();
